<template>
  <div
    v-if="totalPages > 0"
    class="flex items-center justify-center space-x-1"
  >
    <!--
    <UiButton
      color="transparent"
      buttonstyle="rounded-full"
      :disabled="firstPage === null"
      :href="firstPage === null ? '' : props.urlPrefix + hashParams"
    >
      {{ t('first') }}
    </UiButton>
    -->

    <UiButton
      color="transparent"
      buttonstyle="rounded-full"
      :aria-label="t('aria_label_previous')"
      :disabled="prevPage === null"
      :href="prevPage === null ? '' : props.urlPrefix + (prevPage > 1 ? urlPageNumber(prevPage) : '') + hashParams"
    >
      {{ t('previous') }}
    </UiButton>

    <UiButton
      v-for="(pageNr, index) in pageRange"
      :key="index"
      :color="pageNr === currentPage ? 'petrol' : (pageNr === '...' ? 'transparent' : 'light')"
      buttonstyle="rounded-full"
      buttonpadding="px-4 py-2"
      :aria-label="pageNr !== currentPage ? t('go_to_page', { number : pageNr }) : t('current_page', { number : pageNr })"
      :disabled="pageNr === null || pageNr === '...' || pageNr === currentPage"
      :href="pageNr === null || pageNr === '...' || pageNr === currentPage ? '' : props.urlPrefix + (pageNr > 1 ? urlPageNumber(pageNr) : '') + hashParams"
    >
      {{ pageNr }}
    </UiButton>

    <UiButton
      color="transparent"
      buttonstyle="rounded-full"
      :aria-label="t('aria_label_next')"
      :disabled="nextPage === null"
      :href="nextPage === null ? '' : props.urlPrefix + urlPageNumber(nextPage) + hashParams"
    >
      {{ t('next') }}
    </UiButton>

    <!--
    <UiButton
      color="transparent"
      buttonstyle="rounded-full"
      :disabled="lastPage === null"
      :href="lastPage === null ? '' : props.urlPrefix + urlPageNumber(lastPage) + hashParams"
    >
      {{ t('last') }}
    </UiButton>
    -->
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import { useSearchParamsStore } from '~/stores/searchParams'
import UrlHashSearchParams from '~/utils/search/UrlHashSearchParams'

const props = defineProps({
  urlPrefix: {
    type: String,
    default: '/search/'
  },
  currentPage: {
    type: Number,
    default: 0
  },
  totalItems: {
    type: Number,
    default: 0
  },
  itemsPerPage: {
    type: Number,
    default: 12
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const { translate: t } = useTranslations('SharedPagination')

const urlPageNumber = function (pageNumber: number|'...'|null): string {
  if (typeof pageNumber !== 'number') {
    return ''
  }
  const slashBetweenUrlPrefixAndPageNumber = props.urlPrefix?.endsWith('/') ? '' : '/'
  const slashAfterPageNumber = props.urlPrefix?.endsWith('/') ? '/' : ''
  return `${slashBetweenUrlPrefixAndPageNumber}${pageNumber}${slashAfterPageNumber}`
}

// How many pages will there be
const totalPages: ComputedRef<number> = computed(() => {
  return Math.ceil(props.totalItems / props.itemsPerPage)
})
// Go to the last page if the results don't fit (this may be caused by filtering for example)
const currentPage: ComputedRef<number> = computed(() => {
  const exceededLastPage = (props.currentPage > totalPages.value && totalPages.value > 0)
  return exceededLastPage
    ? totalPages.value
    : props.currentPage
})

// // Calculate the offset for the query (Not needed on the frontend)
// const offset: ComputedRef<number> = computed(() => {
//   return (currentPage.value - 1) * props.totalItems
// })
// // Some extra information (Also not needed on the frontend)
// const from: ComputedRef<number> = computed(() => {
//   return offset.value + 1
// })
// const to: ComputedRef<number> = computed(() => {
//   return Math.min((offset.value + props.itemsPerPage), totalPages.value)
// })

const prevPage: ComputedRef<number|null> = computed(() => {
  return currentPage.value > 1
    ? currentPage.value - 1
    : null
})
// const firstPage: ComputedRef<number|null> = computed(() => {
//   return currentPage.value > 1
//     ? 1
//     : null
// })
const nextPage: ComputedRef<number|null> = computed(() => {
  return currentPage.value < totalPages.value
    ? currentPage.value + 1
    : null
})
// const lastPage: ComputedRef<number|null> = computed(() => {
//   return currentPage.value < totalPages.value
//     ? totalPages.value
//     : null
// })

// https://gist.github.com/kottenator/9d936eb3e4e3c3e02598
// https://gist.github.com/kottenator/9d936eb3e4e3c3e02598?permalink_comment_id=2788898#gistcomment-2788898
const pageRange: ComputedRef<(number|'...'|null)[]> = computed(() => {
  const delta = 1
  const range: (number|'...'|null)[] = []
  if (totalPages.value === 0) {
    return range
  }
  for (let i = Math.max(2, (currentPage.value - delta)); i <= Math.min((totalPages.value - 1), (currentPage.value + delta)); i += 1) {
    range.push(i)
  }
  if ((currentPage.value - delta) > 2) {
    range.unshift('...')
  }
  if ((currentPage.value + delta) < (totalPages.value - 1)) {
    range.push('...')
  }
  range.unshift(1)
  if (totalPages.value !== 1) {
    range.push(totalPages.value)
  }
  return range
})

const searchParamsStore = useSearchParamsStore()
const urlHashSearchParams = new UrlHashSearchParams()
const hashParams: ComputedRef<string> = computed(() => {
  if (props.urlPrefix !== '/search/' && props.urlPrefix !== '/lastminute/') {
    return ''
  }
  const hashParamsString = urlHashSearchParams.convertSearchParamsToHashParamsString(searchParamsStore.activeFilters)
  if (hashParamsString === undefined || typeof hashParamsString !== 'string') {
    return ''
  }
  return hashParamsString
})
</script>
